/**
 * Class names shared by every screen rendered inside <AuthLayout />:
 * login, signup, the password flows and the OAuth consent page.
 *
 * The shadcn primitives are themed for a light surface, so on the glass card
 * of AuthLayout they all need an explicit dark treatment. `dark:` overrides are
 * included because that card stays dark whatever the OS colour scheme says.
 */

export const glassInputClassName =
  "bg-white/10 dark:bg-white/10 border-white/20 text-white placeholder:text-gray-300 focus-visible:border-white/40 focus-visible:ring-white/30";

/**
 * Colour overrides for a ra-core <Form> full of <TextInput>s. Pass alongside
 * the page's own spacing, e.g. cn("space-y-4", glassFormClassName).
 *
 * <TextInput> renders its label through <FormLabel> and its error through
 * <FormError>, neither of which accepts a className, so both are recoloured
 * from the <Form> element. The `!` and the attribute selectors are there for
 * specificity, not for style: FormLabel's own `data-[error=true]:text-destructive`
 * compiles to (0,2,0) and so beats a plain `[&_label]` at (0,1,1), and
 * --destructive (#E7000B) measures 1.18:1 against the glass card — on a
 * validation error the label and message would turn invisible.
 * red-200 measures 3.88:1 there, in line with the rest of the card's palette.
 */
export const glassFormClassName =
  "[&_label]:text-white! [&_[data-slot=form-message]]:text-red-200 [&_[aria-invalid=true]]:border-red-300!";

export const ssoButtonClassName =
  "w-full rounded-md border border-white/20 bg-white/10 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-white/20";

// text-white, not the default text-primary-foreground: that token turns
// near-black under the dark theme.
export const authSubmitButtonClassName =
  "w-full cursor-pointer bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700";

export const authLinkClassName =
  "text-sm text-blue-300 hover:text-blue-200 underline";

/** Body copy on the glass card (paragraphs, hints, empty states). */
export const authTextClassName = "text-gray-200";
